export default function (axios) {
  return {
    /**
     * Authenticate a device based on an auth code and network parameters of the mobile client.
     * The network parameters can be obtained by GET parameters which are attached in dedicated
     * networks on the fly.
     *
     * @param {Object} credentials The credentials needed to authenticate the client for using
     * a specific Streamcast device
     * @param {string} credentials.clientIp Part of the network parameters: the client IP
     * @param {string} credentials.clientMac Part of the network parameters: the client MAC
     * @param {string} credentials.siteId Part of the network parameters: the location identifier
     * where the network is operating
     * @param {string} credentials.signature Part of the network parameters: a signature (hash)
     * across all network parameters
     * @param {string} credentials.authenticationCode The code for authenticating the client for
     * a specific device
     * @returns Promise; resolves to device data, rejects for invalid arguments or with axios
     * response
     */
    authenticate: (credentials) => new Promise((resolve, reject) => {
      if (!credentials) {
        reject(new Error('Invalid arguments. Please provide credentials.'));
        return;
      }

      const {
        clientIp, clientMac, siteId, signature, authenticationCode,
      } = credentials;
      if (!clientIp || !clientMac || !siteId || !signature || !authenticationCode) {
        reject(
          new Error(
            'Invalid arguments. Please provide clientIp, clientMac, siteId, signature and authenticationCode.',
          ),
        );
        return;
      }

      axios
        .post('/authenticate', {
          client_ip: clientIp,
          client_mac: clientMac,
          site_id: siteId,
          signature,
          authentication_code: authenticationCode,
        })
        .then((response) => {
          if (response.status !== 200) {
            reject(response);
          }
          resolve(response.data?.data?.device);
        })
        .catch(reject);
    }),
    /**
     * Release a device from the reserved state after successful authentication.
     * This can be used to offer a user-induced end of session (which is optional).
     *
     * @param {int} deviceId The ID of the device to be released. Can be obtained
     * from the answer of .authenticate
     * @returns Promise; resolves to device data, rejects with axios response
     */
    releaseDevice: (deviceId) => new Promise((resolve, reject) => {
      if (!deviceId) {
        reject(new Error('Invalid arguments. Please provide a device id.'));
        return;
      }

      axios
        .post(`/${deviceId}/release`)
        .then((response) => {
          if (response.status !== 200) {
            reject(response);
          }
          resolve(response.data?.data?.device);
        })
        .catch(reject);
    }),
  };
}
